import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  competitionPhases,
  type CompetitionSeasonView,
  type RuleProfileCatalogEntryView,
  type SeasonView,
  type SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { TaskSection } from './TaskSection';
import { apiRequest } from './api';
import { formatCalendarDate } from './athletes';
import { errorKeyOf, type ErrorKey } from './BillingForms';
import {
  catalogCategories,
  chooseCompetition,
  competitionRequest,
  emptyCompetitionForm,
  openSeasons,
  phaseKeys,
  type CompetitionForm,
} from './competitions';
import { EvidenceSection } from './EvidenceSection';
import { ExternalHistorySection } from './ExternalHistorySection';
import { MatchesSection } from './MatchesSection';
import { RegistrationsSection } from './RegistrationsSection';
import { RuleProfilesSection } from './RuleProfilesSection';
import { SuspensionsSection } from './SuspensionsSection';
import { categoryLabel } from './teams';

export interface CompetitionsPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
  /** Holds `competitions.manage`: may create competition seasons. */
  canCreate: boolean;
  /** Holds `rules.manage`: may draft, verify and activate rule profiles. */
  canManageRules: boolean;
  /** Holds `teams.view`: may list the club's seasons for the create form. */
  canListSeasons: boolean;
  /** Holds `athletes.browse`: registrations name athletes, so the server shows them only to such people. */
  canViewRegistrations: boolean;
}

/**
 * Competition seasons of the club and, for the one being looked at, its rule profile versions.
 * Without an activated rule version a competition shows no eligibility approval at all.
 */
export function CompetitionsPanel({
  locale,
  organizationId,
  csrfToken,
  canCreate,
  canManageRules,
  canListSeasons,
  canViewRegistrations,
}: CompetitionsPanelProps): React.JSX.Element {
  const base = `/api/v1/organizations/${organizationId}`;
  const [competitions, setCompetitions] = useState<CompetitionSeasonView[] | null>(null);
  const [seasons, setSeasons] = useState<SeasonView[]>([]);
  const [catalog, setCatalog] = useState<RuleProfileCatalogEntryView[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState<CompetitionForm>(emptyCompetitionForm);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    try {
      const loaded = await apiRequest<CompetitionSeasonView[]>(`${base}/competition-seasons`);
      setCompetitions(loaded);
      setSelectedId((current) => chooseCompetition(loaded, current));
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void apiRequest<RuleProfileCatalogEntryView[]>(`${base}/rule-profiles/catalog`)
      .then(setCatalog)
      .catch((error: unknown) => {
        setErrorKey(errorKeyOf(error));
      });
  }, [base]);

  useEffect(() => {
    if (!canCreate || !canListSeasons) return;
    void apiRequest<SeasonView[]>(`${base}/seasons`)
      .then((loaded) => {
        setSeasons(openSeasons(loaded));
      })
      .catch((error: unknown) => {
        setErrorKey(errorKeyOf(error));
      });
  }, [base, canCreate, canListSeasons]);

  const request = competitionRequest(form);

  async function create(): Promise<void> {
    if (request === null) return;
    setErrorKey(null);
    setDone(false);
    try {
      const created = await apiRequest<CompetitionSeasonView>(`${base}/competition-seasons`, {
        method: 'POST',
        body: request,
        csrfToken,
      });
      setForm(emptyCompetitionForm);
      setDone(true);
      await load();
      setSelectedId(created.id);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  function togglePhase(phase: (typeof competitionPhases)[number], checked: boolean): void {
    setForm((current) => ({
      ...current,
      phases: competitionPhases.filter((candidate) =>
        candidate === phase ? checked : current.phases.includes(candidate),
      ),
    }));
  }

  const selected = competitions?.find((competition) => competition.id === selectedId) ?? null;

  return (
    <Box component="section" aria-labelledby="competitions-title" sx={{ mb: 3 }}>
      <Typography variant="h5" component="h2" id="competitions-title" gutterBottom>
        {translate(locale, 'competitions.seasons.title')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {competitions !== null && competitions.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {translate(locale, 'competitions.seasons.empty')}
        </Typography>
      )}
      {competitions !== null && competitions.length > 0 && (
        <Stack
          component="ul"
          spacing={1}
          sx={{ listStyle: 'none', padding: 0, margin: 0, marginBottom: 3 }}
        >
          {competitions.map((competition) => (
            <Box component="li" key={competition.id}>
              <Button
                variant={competition.id === selectedId ? 'contained' : 'outlined'}
                onClick={() => {
                  setSelectedId(competition.id);
                }}
                aria-pressed={competition.id === selectedId}
                sx={{ textAlign: 'left', justifyContent: 'flex-start' }}
              >
                {competition.name}
              </Button>
              <Typography variant="body2" color="text.secondary">
                {competition.seasonLabel} · {categoryLabel(locale, competition.category)}
                {competition.division === null ? '' : ` · ${competition.division}`} ·{' '}
                {competition.phases.map((phase) => translate(locale, phaseKeys[phase])).join(', ')}{' '}
                · {competition.timeZone}
              </Typography>
              <Typography variant="body2">
                {competition.activeRuleProfile === null
                  ? translate(locale, 'competitions.rules.none')
                  : `${translate(locale, 'competitions.rules.active')} ${String(competition.activeRuleProfile.versionNumber)} · ${formatCalendarDate(competition.activeRuleProfile.activatedAt.slice(0, 10), locale)}`}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
      {done && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {translate(locale, 'competitions.seasons.done')}
        </Alert>
      )}
      {canCreate && (
        <TaskSection locale={locale} titleKey="competitions.seasons.create.title">
          <Stack
            component="form"
            spacing={2}
            sx={{ maxWidth: 480, mb: 3 }}
            onSubmit={(event) => {
              event.preventDefault();
              void create();
            }}
          >
            <Typography variant="subtitle1" component="h3">
              {translate(locale, 'competitions.seasons.create.title')}
            </Typography>
            <TextField
              required
              select
              size="small"
              label={translate(locale, 'competitions.field.season')}
              value={form.seasonId}
              onChange={(event) => {
                setForm({ ...form, seasonId: event.target.value });
              }}
            >
              {seasons.map((season) => (
                <MenuItem key={season.id} value={season.id}>
                  {season.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              size="small"
              label={translate(locale, 'competitions.field.name')}
              value={form.name}
              onChange={(event) => {
                setForm({ ...form, name: event.target.value });
              }}
              slotProps={{ htmlInput: { maxLength: 200 } }}
            />
            <TextField
              required
              size="small"
              label={translate(locale, 'competitions.field.federation')}
              value={form.federation}
              onChange={(event) => {
                setForm({ ...form, federation: event.target.value });
              }}
              slotProps={{ htmlInput: { maxLength: 50 } }}
            />
            <TextField
              required
              select
              size="small"
              label={translate(locale, 'competitions.field.category')}
              value={form.category}
              onChange={(event) => {
                setForm({ ...form, category: event.target.value });
              }}
            >
              {catalogCategories(catalog).map((category) => (
                <MenuItem key={category} value={category}>
                  {categoryLabel(locale, category)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label={translate(locale, 'competitions.field.division')}
              value={form.division}
              onChange={(event) => {
                setForm({ ...form, division: event.target.value });
              }}
              slotProps={{ htmlInput: { maxLength: 100 } }}
            />
            <TextField
              size="small"
              label={translate(locale, 'competitions.field.format')}
              value={form.format}
              onChange={(event) => {
                setForm({ ...form, format: event.target.value });
              }}
              slotProps={{ htmlInput: { maxLength: 100 } }}
            />
            <Box role="group" aria-label={translate(locale, 'competitions.field.phases')}>
              {competitionPhases.map((phase) => (
                <FormControlLabel
                  key={phase}
                  control={
                    <Checkbox
                      checked={form.phases.includes(phase)}
                      onChange={(event) => {
                        togglePhase(phase, event.target.checked);
                      }}
                    />
                  }
                  label={translate(locale, phaseKeys[phase])}
                />
              ))}
            </Box>
            <Box>
              <Button type="submit" variant="contained" disabled={request === null}>
                {translate(locale, 'competitions.seasons.submit')}
              </Button>
            </Box>
          </Stack>
        </TaskSection>
      )}
      {selected !== null && (
        <TaskSection
          locale={locale}
          titleKey="competitions.profiles.title"
          descriptionKey="task.rules.description"
        >
          <RuleProfilesSection
            locale={locale}
            base={base}
            csrfToken={csrfToken}
            competition={selected}
            catalog={catalog}
            canManageRules={canManageRules}
            onChanged={load}
          />
        </TaskSection>
      )}
      {selected !== null && (
        <TaskSection
          locale={locale}
          titleKey="match.title"
          descriptionKey="task.matches.description"
          defaultOpen
        >
          <MatchesSection
            locale={locale}
            base={base}
            csrfToken={csrfToken}
            competition={selected}
            canViewRoster={canViewRegistrations}
          />
        </TaskSection>
      )}
      {selected !== null && canViewRegistrations && (
        <TaskSection
          locale={locale}
          titleKey="registrations.title"
          descriptionKey="task.registrations.description"
        >
          <RegistrationsSection
            locale={locale}
            base={base}
            csrfToken={csrfToken}
            competition={selected}
          />
        </TaskSection>
      )}
      {selected !== null && canViewRegistrations && (
        <TaskSection
          locale={locale}
          titleKey="evidence.title"
          descriptionKey="task.evidence.description"
        >
          <EvidenceSection
            locale={locale}
            base={base}
            csrfToken={csrfToken}
            competition={selected}
          />
        </TaskSection>
      )}
      {selected !== null && canViewRegistrations && (
        <TaskSection
          locale={locale}
          titleKey="suspension.title"
          descriptionKey="task.suspensions.description"
        >
          <SuspensionsSection
            locale={locale}
            base={base}
            csrfToken={csrfToken}
            competition={selected}
          />
        </TaskSection>
      )}
      {canViewRegistrations && (
        <TaskSection
          locale={locale}
          titleKey="externalHistory.title"
          descriptionKey="task.history.description"
        >
          <ExternalHistorySection locale={locale} base={base} csrfToken={csrfToken} />
        </TaskSection>
      )}
    </Box>
  );
}
